import { H1, H2, Li, P, Strong, Ul } from '../Prose';
export default function SectionDesignFileOrganisation() {
  return (
    <>
      <P>Design</P>
      <H1>File Organisation</H1>

      <P>
        Design files and assets should aim to follow a predictable organisation
        across clients, so we can jump between projects and know where to look
        in each.
      </P>

      <H2>Dropbox</H2>

      <P>
        When it's possible, uploading raw JPG/PNG image files to Dropbox is a
        good option, as there will never be any issues accessing these years in
        the future, unlike proprietary file formats which may not be usable in
        future if the software changes and/or we don't continue to hold the
        necessary licenses.
      </P>

      <Ul>
        <Li>
          <Strong>Clients/[Client]/Screenshots/[YYYY]/</Strong> - Screenshots of
          old or current versions of the website/apps. For future reference, use
          in portfolios, etc. These are normally taken by QA, but can be used as
          references for the designer.
        </Li>
        <Li>
          <Strong>
            Clients/[Client]/Projects/[YYYY-MM] [Project]/Figma Designs/
          </Strong>{' '}
          - Exports of designs at key milestones in the project (e.g. when the
          design is sent to the client for review). Prefix the filename with
          YYYY-MM-DD so the progression over time is clear to see. Some images
          may be very large if they are the export of a whole Figma Section, but
          there shouldn't really be a problem zooming/panning large PNGs.
        </Li>
      </Ul>

      <H2>Figma - Client</H2>

      <P>
        Each project will generally have a <Strong>client-facing</Strong> Figma
        file, for the client to review and sign off. This will be the same one
        handed over to developers, as they should also be building what has been
        agreed on by the client.
      </P>

      <P>
        If a project is just an extension of an existing design — such as adding
        a new section to a mobile app — it’s generally best to continue working
        within the same Figma file. However, if the project differs
        significantly (e.g. a Mobile App and a Website), it may make sense to
        split into a new Figma file.
      </P>

      <P>
        As a rule, design components are typically stored in the same file where
        they are used. For larger projects, though, it can make sense to create
        a separate library file, particularly when components are used across
        multiple files.
      </P>

      <P>
        An example name would be <Strong>Bloom43 Mobile App</Strong>, and the
        Pages in the file will typically be:
      </P>

      <Ul>
        <Li>
          <Strong>Wireframes</Strong> - All Wireframes created for the project.
        </Li>
        <Li>
          <Strong>Design</Strong> - The main screen designs of the app/website.
          <Ul>
            <Li>
              Keep duplication of screens here to a minimum. Ideally each screen
              should only ever be shown in one frame.
            </Li>
            <Li>
              Any major variants / alternate states of each screen can be shown
              with clear labelling to explain the variation.
            </Li>
          </Ul>
        </Li>
        <Li>
          <Strong>WIP</Strong> (optional) - If we’re working on new features for
          an already implemented project, a WIP page will make sense. Here we
          have the space to try out different design options, without changing
          anything on the main design page. As soon as something has been signed
          off by the client and implemented by our developers, it can be moved
          to the Design page. The Design page should try to always show the
          state of the live website/app.
        </Li>
        <Li>
          <Strong>UI Kit</Strong> - All styles and reusable components used in
          the designs. Must be kept clean with only single currently used
          version of each component. Everything should be clearly labeled.
        </Li>
        <Li>
          <Strong>Inspiration</Strong> (Optional) - All research into competitor
          apps can be stored here. This can be conceptual and/or visual
          inspiration. If we receive a moodboard from the client, this should be
          stored here as well – and clearly marked in a section named “by
          [client]”. If it's been decided that we'll create a moodboard for the
          client to help define the desired look & feel of the project, this can
          be developed here as well. Any unapproved references should be
          deleted, but the approved ones can remain organised on this page.
        </Li>
        <Li>
          <Strong>Archive</Strong> (Optional) - A place to store design options
          that we’ve decided against, but might still be useful for the future.
          Basically, this is a place to save everything that you don’t want to
          completely delete (and that might be difficult to find via the version
          history later on). If necessary, components can be detached in the
          Archive to keep the ‘old’ version.
        </Li>
        <Li>
          <Strong>Future Releases</Strong> (Optional) - If something has already
          been designed, but it has been decided to not implement it with the
          next release, we can store it here.
        </Li>
        <Li>
          <Strong>Cover</Strong> - The figma cover design. We always aim to add
          covers to all projects, to keep them visually organised. The cover
          should show the client’s logo and the project name (e.g. Mobile App)
        </Li>
      </Ul>

      <P>
        We try to work with sections inside these pages, but if the project is
        getting particularly large, the pages can also be split, e.g.:
      </P>

      <Ul>
        <Li>Design - Main App</Li>
        <Li>Design - Checkout</Li>
        <Li>Design - Rewards System</Li>
      </Ul>

      <H2>Figma - Internal</H2>

      <P>
        If it's necessary to keep extra designs for the designer's internal
        reference only, these can be kept in a separate Figma file, e.g.{' '}
        <Strong>Bloom43 Mobile App - Internal</Strong>
      </P>
    </>
  );
}
